import { z } from 'zod/v4';

/**
 * One discount line on a promo code. Mirrors the `discounts[]` shape of
 * `PromoCode` in `common/types/promo-codes.type.ts` (kept there for the
 * `sp-form-additional-settings*` libs, which can't depend on `@integrations`).
 */
export const PromoCodeDiscountContract = z.object({
    type: z.string().catch(''),
    value: z.coerce.number().catch(0),
    currency: z.string().catch(''),
});

/**
 * A CRM promo code. Mirrors `PromoCode` from
 * `common/types/promo-codes.type.ts` so the query result stays assignable to
 * the lib components that still type against the common shape.
 *
 * Only `id`, `promoCode` and `discounts` are load-bearing — the picker renders the
 * code, its first discount, and submits the id. Every other field is `.catch()`-ed
 * to a typed default: they exist for documentation and for the common shape, and a
 * picker that loses all nine promo codes over an unread `status` or a null
 * `activeTo` is a far worse outcome than a defaulted field nobody reads.
 */
export const PromoCodeContract = z.object({
    id: z.coerce.number(),
    userId: z.coerce.number().catch(0),
    promoCode: z.string(),
    discounts: z.preprocess((value) => value ?? [], z.array(PromoCodeDiscountContract)),
    maxUses: z.coerce.number().catch(0),
    activeFrom: z.string().catch(''),
    activeTo: z.string().catch(''),
    timeZone: z.string().catch(''),
    status: z.coerce.number().catch(0),
    used: z.coerce.number().catch(0),
});

/**
 * Unwraps whatever envelope the promo-code endpoint answers with. The
 * landings-service CRM proxy replies `{ data: { total, items } }`, while the
 * underlying crm-service replies `{ total, items }` and older builds replied with
 * a bare array — all three reach this schema depending on deploy, and the caller
 * only ever wants the list. Anything unrecognised falls through unchanged so the
 * array validation below reports it instead of silently yielding an empty list.
 */
export function unwrapPromoCodeCollection(response: unknown): unknown {
    let current = response;

    if (current && typeof current === 'object' && !Array.isArray(current) && 'data' in current) {
        current = (current as { data: unknown }).data;
    }

    if (current && typeof current === 'object' && !Array.isArray(current) && 'items' in current) {
        current = (current as { items: unknown }).items;
    }

    return current;
}

export const PromoCodeCollectionResponseSchema = z.preprocess(unwrapPromoCodeCollection, z.array(PromoCodeContract));

export type PromoCodeDiscount = z.infer<typeof PromoCodeDiscountContract>;
export type PromoCode = z.infer<typeof PromoCodeContract>;

/**
 * Keeps whichever promo codes do satisfy the contract when the collection as a whole
 * does not. Non-strict `parseResponse` reports the mismatch and hands the payload back
 * untouched; without this the picker would render nothing at all because of one
 * malformed record.
 */
export function salvagePromoCodes(response: unknown): PromoCode[] {
    const unwrapped = unwrapPromoCodeCollection(response);

    if (!Array.isArray(unwrapped)) {
        return [];
    }

    return unwrapped
        .map((item) => PromoCodeContract.safeParse(item))
        .filter((result) => result.success)
        .map((result) => result.data);
}
